import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { StatsCard } from '../components/ui/StatsCard';

const ICON = (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const fmtMoney = (cents: any, currency = 'usd') => {
  const n = Number(cents || 0) / 100;
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() });
};
const when = (epochOrIso: any) => {
  if (epochOrIso == null) return '—';
  if (typeof epochOrIso === 'number') return new Date(epochOrIso * 1000).toLocaleString();
  return new Date(epochOrIso).toLocaleString();
};

type TabKey =
  | 'customers' | 'subscriptions' | 'invoices' | 'charges' | 'refunds'
  | 'disputes' | 'payouts' | 'products' | 'prices' | 'meters'
  | 'coupons' | 'webhooks';

const TABS: Array<{ key: TabKey; label: string; endpoint: string }> = [
  { key: 'customers',     label: 'Customers',     endpoint: '/stripe/customers?limit=100' },
  { key: 'subscriptions', label: 'Subscriptions', endpoint: '/stripe/subscriptions?limit=100' },
  { key: 'invoices',      label: 'Invoices',      endpoint: '/stripe/invoices?limit=100' },
  { key: 'charges',       label: 'Charges',       endpoint: '/stripe/charges?limit=100' },
  { key: 'refunds',       label: 'Refunds',       endpoint: '/stripe/refunds?limit=100' },
  { key: 'disputes',      label: 'Disputes',      endpoint: '/stripe/disputes?limit=100' },
  { key: 'payouts',       label: 'Payouts',       endpoint: '/stripe/payouts?limit=100' },
  { key: 'products',      label: 'Products',      endpoint: '/stripe/products?limit=100' },
  { key: 'prices',        label: 'Prices',        endpoint: '/stripe/prices?limit=100' },
  { key: 'meters',        label: 'Meters',        endpoint: '/stripe/meters?limit=100' },
  { key: 'coupons',       label: 'Coupons',       endpoint: '/stripe/coupons?limit=100' },
  { key: 'webhooks',      label: 'Webhooks',      endpoint: '/stripe/webhook-endpoints' },
];

export const StripePage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('customers');
  const [filter, setFilter] = useState('');
  const [data, setData] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<any>(null);
  const [account, setAccount] = useState<any>(null);

  const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
  const token = localStorage.getItem('intrlock_token') || '';
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  // One-time fetch for the header (balance + account)
  useEffect(() => {
    fetch(`${API}/stripe/balance`, { headers })
      .then(r => r.json())
      .then(d => { setBalance(d.balance); setAccount(d.account); })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Lazy-load whichever tab is active (and cache the result in `data`)
  useEffect(() => {
    if (data[tab]) return;
    const t = TABS.find(x => x.key === tab)!;
    setLoading(true); setError(null);
    fetch(`${API}${t.endpoint}`, { headers })
      .then(r => r.json())
      .then(d => {
        if (d?.error) setError(d.error);
        // Endpoints return either { data: [...] } (charges/refunds/etc.)
        // or a direct array (customers/invoices/subscriptions/products/prices).
        const rows = Array.isArray(d) ? d : (d?.data || []);
        setData(prev => ({ ...prev, [tab]: rows }));
      })
      .catch(e => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [tab]);

  const rows = data[tab] || [];
  const filtered = useMemo(() => {
    if (!filter) return rows;
    const f = filter.toLowerCase();
    return rows.filter((r: any) => JSON.stringify(r).toLowerCase().includes(f));
  }, [rows, filter]);

  // Live balance summary in the header
  const available = balance?.available?.[0];
  const pending   = balance?.pending?.[0];
  const liveMode  = account?.livemode === true;

  return (
    <div className="shadow-page-frame legacy-dashboard-migrated space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Stripe</h1>
        <p className="text-gray-500 mt-1">Live read-only view of every Stripe entity connected to the Intrlock account.</p>
        {account && (
          <div className="flex items-center gap-3 mt-2 text-xs">
            <Badge color={liveMode ? 'green' : 'amber'}>{liveMode ? 'LIVE MODE' : 'TEST / SANDBOX'}</Badge>
            <span className="text-gray-500">Account:</span>
            <span className="font-mono">{account.id}</span>
            {account.business_profile?.name && <span className="text-gray-700">· {account.business_profile.name}</span>}
            {account.country && <span className="text-gray-500">· {account.country}</span>}
          </div>
        )}
      </div>

      {balance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard title="Available Balance" value={available ? fmtMoney(available.amount, available.currency) : '$0.00'} icon={ICON} />
          <StatsCard title="Pending Balance"   value={pending   ? fmtMoney(pending.amount,   pending.currency)   : '$0.00'} icon={ICON} />
          <StatsCard title="Currencies"        value={(balance.available || []).map((b: any) => b.currency.toUpperCase()).join(', ') || '—'} icon={ICON} />
          <StatsCard title="Charges Enabled"   value={account?.charges_enabled ? 'Yes' : 'No'} icon={ICON} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === t.key
              ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {t.label}
            {data[t.key] && <span className="ml-1 text-[10px] opacity-70">({data[t.key].length})</span>}
          </button>
        ))}
        <div className="flex-1" />
        <input
          value={filter} onChange={e => setFilter(e.target.value)}
          placeholder="Filter…"
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-56"
        />
      </div>

      <Card padding={false}>
        <CardHeader title={TABS.find(t => t.key === tab)!.label} subtitle={`Live from Stripe via ${TABS.find(t => t.key === tab)!.endpoint}`} />
        {loading ? <div className="p-6 text-sm text-gray-400">Loading from Stripe…</div>
         : error ? <div className="p-6 text-sm text-red-500">Error: {error}</div>
         : filtered.length === 0 ? <div className="p-6 text-sm text-gray-400">No {tab} found.</div>
         : <TabContent tab={tab} rows={filtered} />}
      </Card>
    </div>
  );
};

// Expandable list row — click to reveal a line-item table beneath.
const LineItemRow: React.FC<{
  summary: React.ReactNode;
  lineItems: Array<{ description: string; unit_amount: number | null; quantity: number; amount: number; currency: string; period?: string | null }>;
  total: number;
  currency: string;
  fees?: number | null;
}> = ({ summary, lineItems, total, currency, fees }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="px-4 py-3 grid grid-cols-12 items-center gap-3 hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(!open)}>
        <button className="text-emerald-600 text-xs w-6">{open ? '▼' : '▶'}</button>
        <div className="col-span-11">{summary}</div>
      </div>
      {open && (
        <div className="bg-gray-50 px-12 py-3 border-t border-gray-100">
          {lineItems.length === 0 ? (
            <div className="text-xs text-gray-400">No line items.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-1 pr-2">Description</th>
                  <th className="py-1 pr-2">Unit Price</th>
                  <th className="py-1 pr-2">Qty</th>
                  <th className="py-1 pr-2">Extended</th>
                  <th className="py-1 pr-2">Period</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <tr key={i} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-1.5 pr-2">{li.description}</td>
                    <td className="py-1.5 pr-2 font-mono">{li.unit_amount != null ? fmtMoney(li.unit_amount, li.currency) : '—'}</td>
                    <td className="py-1.5 pr-2 font-mono">{li.quantity}</td>
                    <td className="py-1.5 pr-2 font-mono font-semibold">{fmtMoney(li.amount, li.currency)}</td>
                    <td className="py-1.5 pr-2 text-gray-500">{li.period || '—'}</td>
                  </tr>
                ))}
                <tr className="border-t border-gray-300 bg-white">
                  {fees != null && fees > 0 ? (
                    <>
                      <td colSpan={2} className="py-1.5 pr-2 text-right text-gray-500">Stripe fee</td>
                      <td colSpan={2} className="py-1.5 pr-2 font-mono">{fmtMoney(fees, currency)}</td>
                      <td />
                    </>
                  ) : null}
                </tr>
                <tr className="border-t-2 border-gray-400 bg-white">
                  <td colSpan={3} className="py-2 pr-2 text-right font-semibold text-gray-700">TOTAL</td>
                  <td className="py-2 pr-2 font-mono font-bold text-emerald-700">{fmtMoney(total, currency)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// Per-tab columns. Each renderer picks the most useful fields from the
// Stripe object for that resource type.
const TabContent: React.FC<{ tab: TabKey; rows: any[] }> = ({ tab, rows }) => {
  const data = rows.map((r: any, i: number) => ({ ...r, _key: r.id || i }));

  if (tab === 'customers') return (
    <Table data={data.map(r => ({ ...r, id: r._key }))} columns={[
      { key: 'id',      header: 'ID',      render: (r: any) => <span className="font-mono text-xs">{r.id}</span> },
      { key: 'name',    header: 'Name',    render: (r: any) => <span className="text-sm">{r.name || '—'}</span> },
      { key: 'email',   header: 'Email',   render: (r: any) => <span className="text-xs text-gray-600">{r.email || '—'}</span> },
      { key: 'created', header: 'Created', render: (r: any) => <span className="text-xs text-gray-500">{when(r.created)}</span> },
      { key: 'balance', header: 'Balance', render: (r: any) => <span className="font-mono text-xs">{fmtMoney(r.balance, r.currency)}</span> },
      { key: 'delinquent', header: 'Status', render: (r: any) => r.delinquent ? <Badge color="red">Delinquent</Badge> : <Badge color="green">OK</Badge> },
    ]} />
  );

  if (tab === 'subscriptions') {
    const grand = rows.reduce((a, r) => a + Number(r.items?.data?.reduce((s: number, i: any) => s + (Number(i.price?.unit_amount || 0) * Number(i.quantity || 1)), 0) || 0), 0);
    return (
      <div>
        {data.map((r: any) => {
          const items = (r.items?.data || []).map((i: any) => {
            const productName = (typeof i.price?.product === 'object' ? i.price.product?.name : null) || i.price?.nickname || i.price?.id || 'Subscription item';
            const unit = Number(i.price?.unit_amount || 0);
            const qty  = Number(i.quantity || 1);
            const recur = i.price?.recurring ? `${i.price.recurring.interval_count} ${i.price.recurring.interval}` : null;
            const usage = i.price?.recurring?.usage_type;
            return {
              description: `${productName}${recur ? ` (per ${recur})` : ''}${usage === 'metered' ? ' · metered' : ''}`,
              unit_amount: unit,
              quantity: qty,
              amount: unit * qty,
              currency: i.price?.currency || r.currency || 'usd',
              period: r.current_period_start && r.current_period_end
                ? `${new Date(r.current_period_start * 1000).toLocaleDateString()} → ${new Date(r.current_period_end * 1000).toLocaleDateString()}`
                : null,
            };
          });
          const total = items.reduce((a: number, x: any) => a + x.amount, 0);
          return (
            <LineItemRow
              key={r._key}
              total={total}
              currency={r.currency || 'usd'}
              lineItems={items}
              summary={
                <div className="grid grid-cols-6 gap-3 items-center text-xs">
                  <span className="font-mono">{r.id}</span>
                  <span className="font-mono">{r.customer}</span>
                  <Badge color={r.status === 'active' ? 'green' : r.status === 'canceled' ? 'gray' : 'amber'}>{r.status}</Badge>
                  <span className="text-gray-600">{items.length} item{items.length === 1 ? '' : 's'}</span>
                  <span className="text-gray-500">renews {when(r.current_period_end)}</span>
                  <span className="font-mono font-semibold text-emerald-700">{fmtMoney(total, r.currency)}</span>
                </div>
              }
            />
          );
        })}
        <div className="px-4 py-3 border-t-2 border-gray-300 bg-gray-50 flex justify-end gap-6 text-sm">
          <span className="text-gray-500">Total of all subscriptions (per period):</span>
          <span className="font-mono font-bold text-emerald-700">{fmtMoney(grand)}</span>
        </div>
      </div>
    );
  }

  if (tab === 'invoices') {
    const grand = rows.reduce((a, r) => a + Number(r.total || 0), 0);
    return (
      <div>
        {data.map((r: any) => {
          const items = (r.lines?.data || []).map((li: any) => {
            const product = typeof li.price?.product === 'object' ? li.price.product?.name : null;
            return {
              description: li.description || product || li.price?.nickname || li.price?.id || 'Line item',
              unit_amount: li.price?.unit_amount ?? (li.amount && li.quantity ? Math.round(li.amount / li.quantity) : null),
              quantity:    Number(li.quantity || 1),
              amount:      Number(li.amount || 0),
              currency:    li.currency || r.currency || 'usd',
              period:      li.period?.start && li.period?.end
                ? `${new Date(li.period.start * 1000).toLocaleDateString()} → ${new Date(li.period.end * 1000).toLocaleDateString()}`
                : null,
            };
          });
          return (
            <LineItemRow
              key={r._key}
              total={Number(r.total || 0)}
              currency={r.currency || 'usd'}
              lineItems={items}
              fees={r.tax || null}
              summary={
                <div className="grid grid-cols-6 gap-3 items-center text-xs">
                  <span className="font-mono">{r.number || r.id}</span>
                  <span className="font-mono">{r.customer || '—'}</span>
                  <Badge color={r.status === 'paid' ? 'green' : r.status === 'open' ? 'amber' : 'gray'}>{r.status}</Badge>
                  <span className="text-gray-500">{when(r.created)}</span>
                  <div className="flex gap-2 text-xs">
                    {r.hosted_invoice_url && <a href={r.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline" onClick={e => e.stopPropagation()}>View</a>}
                    {r.invoice_pdf && <a href={r.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline" onClick={e => e.stopPropagation()}>PDF</a>}
                  </div>
                  <span className="font-mono font-semibold text-emerald-700">{fmtMoney(r.total, r.currency)}</span>
                </div>
              }
            />
          );
        })}
        <div className="px-4 py-3 border-t-2 border-gray-300 bg-gray-50 flex justify-end gap-6 text-sm">
          <span className="text-gray-500">Total of all invoices:</span>
          <span className="font-mono font-bold text-emerald-700">{fmtMoney(grand)}</span>
        </div>
      </div>
    );
  }

  // Charges / refunds / disputes / payouts: each is a single transaction —
  // so the "line items" view is a one-line breakdown with qty=1 and total
  // pulled from the object itself. Stripe charges also carry a fee.
  if (tab === 'charges' || tab === 'refunds' || tab === 'disputes' || tab === 'payouts') {
    const grand = rows.reduce((a, r) => a + Number(r.amount || 0), 0);
    const statusColor = (s: string) =>
      s === 'paid' || s === 'succeeded' || s === 'won' ? 'green'
      : s === 'failed' || s === 'lost'    ? 'red'
      : 'amber';
    const subjectLabel = tab.charAt(0).toUpperCase() + tab.slice(1, -1);
    return (
      <div>
        {data.map((r: any) => {
          const lineItems = [{
            description: r.description || (r.reason ? `${subjectLabel}: ${r.reason}` : subjectLabel),
            unit_amount: Number(r.amount || 0),
            quantity:    1,
            amount:      Number(r.amount || 0),
            currency:    r.currency || 'usd',
            period:      null,
          }];
          const fees = r.balance_transaction?.fee || null;
          return (
            <LineItemRow
              key={r._key}
              total={Number(r.amount || 0)}
              currency={r.currency || 'usd'}
              lineItems={lineItems}
              fees={fees}
              summary={
                <div className="grid grid-cols-6 gap-3 items-center text-xs">
                  <span className="font-mono">{r.id}</span>
                  <span className="font-mono">{r.customer || r.charge || '—'}</span>
                  <Badge color={statusColor(r.status)}>{r.status}</Badge>
                  <span className="text-gray-500">{when(r.created || r.arrival_date)}</span>
                  <span className="text-xs text-gray-600 truncate">{r.description || r.reason || r.method || '—'}</span>
                  <span className="font-mono font-semibold text-emerald-700">{fmtMoney(r.amount, r.currency)}</span>
                </div>
              }
            />
          );
        })}
        <div className="px-4 py-3 border-t-2 border-gray-300 bg-gray-50 flex justify-end gap-6 text-sm">
          <span className="text-gray-500">Total of all {tab}:</span>
          <span className="font-mono font-bold text-emerald-700">{fmtMoney(grand)}</span>
        </div>
      </div>
    );
  }

  if (tab === 'products') return (
    <Table data={data.map(r => ({ ...r, id: r._key }))} columns={[
      { key: 'id',     header: 'ID',     render: (r: any) => <span className="font-mono text-xs">{r.id}</span> },
      { key: 'name',   header: 'Name',   render: (r: any) => <span className="text-sm font-medium">{r.name}</span> },
      { key: 'active', header: 'Active', render: (r: any) => r.active ? <Badge color="green">Yes</Badge> : <Badge color="gray">No</Badge> },
      { key: 'type',   header: 'Type',   render: (r: any) => <span className="text-xs">{r.type || 'service'}</span> },
      { key: 'desc',   header: 'Description', render: (r: any) => <span className="text-xs text-gray-600">{r.description || '—'}</span> },
      { key: 'meter',  header: 'Meter',  render: (r: any) => <span className="font-mono text-xs">{r.metadata?.meter_key || '—'}</span> },
    ]} />
  );

  if (tab === 'prices') return (
    <Table data={data.map(r => ({ ...r, id: r._key }))} columns={[
      { key: 'id',       header: 'ID',       render: (r: any) => <span className="font-mono text-xs">{r.id}</span> },
      { key: 'product',  header: 'Product',  render: (r: any) => <span className="font-mono text-xs">{r.product}</span> },
      { key: 'unit',     header: 'Unit Amount', render: (r: any) => <span className="font-mono text-sm">{r.unit_amount != null ? fmtMoney(r.unit_amount, r.currency) : '—'}</span> },
      { key: 'recurring',header: 'Recurring',render: (r: any) => r.recurring ? <span className="text-xs">{r.recurring.interval_count} {r.recurring.interval}</span> : <span className="text-xs text-gray-400">one-time</span> },
      { key: 'usage',    header: 'Usage Type',render: (r: any) => <span className="text-xs">{r.recurring?.usage_type || '—'}</span> },
      { key: 'active',   header: 'Active',   render: (r: any) => r.active ? <Badge color="green">Yes</Badge> : <Badge color="gray">No</Badge> },
    ]} />
  );

  if (tab === 'meters') return (
    <Table data={data.map(r => ({ ...r, id: r._key }))} columns={[
      { key: 'id',      header: 'ID',      render: (r: any) => <span className="font-mono text-xs">{r.id}</span> },
      { key: 'name',    header: 'Display Name', render: (r: any) => <span className="text-sm font-medium">{r.display_name}</span> },
      { key: 'event',   header: 'Event Name', render: (r: any) => <span className="font-mono text-xs">{r.event_name}</span> },
      { key: 'agg',     header: 'Aggregation', render: (r: any) => <span className="text-xs">{r.default_aggregation?.formula || 'sum'}</span> },
      { key: 'status',  header: 'Status', render: (r: any) => <Badge color={r.status === 'active' ? 'green' : 'gray'}>{r.status}</Badge> },
      { key: 'created', header: 'Created', render: (r: any) => <span className="text-xs">{when(r.created)}</span> },
    ]} />
  );

  if (tab === 'coupons') return (
    <Table data={data.map(r => ({ ...r, id: r._key }))} columns={[
      { key: 'id',       header: 'ID',       render: (r: any) => <span className="font-mono text-xs">{r.id}</span> },
      { key: 'name',     header: 'Name',     render: (r: any) => <span className="text-sm">{r.name}</span> },
      { key: 'discount', header: 'Discount', render: (r: any) => r.percent_off
        ? <span className="font-mono text-sm">{r.percent_off}% off</span>
        : <span className="font-mono text-sm">{fmtMoney(r.amount_off, r.currency)} off</span> },
      { key: 'duration', header: 'Duration', render: (r: any) => <span className="text-xs">{r.duration}{r.duration_in_months ? ` (${r.duration_in_months} mo)` : ''}</span> },
      { key: 'valid',    header: 'Valid?',   render: (r: any) => r.valid ? <Badge color="green">Yes</Badge> : <Badge color="gray">No</Badge> },
      { key: 'redeemed', header: 'Redeemed', render: (r: any) => <span className="font-mono text-xs">{r.times_redeemed}{r.max_redemptions ? ` / ${r.max_redemptions}` : ''}</span> },
    ]} />
  );

  if (tab === 'webhooks') return (
    <Table data={data.map(r => ({ ...r, id: r._key }))} columns={[
      { key: 'id',     header: 'ID',     render: (r: any) => <span className="font-mono text-xs">{r.id}</span> },
      { key: 'url',    header: 'URL',    render: (r: any) => <span className="font-mono text-xs text-emerald-700 break-all">{r.url}</span> },
      { key: 'events', header: 'Events', render: (r: any) => <span className="text-xs">{(r.enabled_events || []).slice(0,3).join(', ')}{(r.enabled_events || []).length > 3 ? ` (+${r.enabled_events.length-3})` : ''}</span> },
      { key: 'status', header: 'Status', render: (r: any) => <Badge color={r.status === 'enabled' ? 'green' : 'gray'}>{r.status}</Badge> },
      { key: 'api_v',  header: 'API Ver',  render: (r: any) => <span className="text-xs">{r.api_version || '—'}</span> },
    ]} />
  );

  return <pre className="p-4 text-xs">{JSON.stringify(rows.slice(0, 5), null, 2)}</pre>;
};
